import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ColumnDef } from '@tanstack/react-table';

import { NSButton, NSSwitch } from 'bricks';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { ExpandableSection } from 'components/ExpandableSection/ExpandableSection';
import RenderIf from 'components/RenderIf/RenderIf';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { HIDE_WARNINGS_KEY, OPERATIONS_EXPANDABLE_SECTIONS, tableValueClassName } from 'views/ProFormaTable/constants';
import { formatCashFlowPeriods, getSaleIndexForCadence } from 'views/ProFormaTable/helpers';
import { BOEProFormaDiscrepancies } from 'views/ProFormaTable/types';
import NSSettingsMenu from 'bricks/NSSettingsMenu/NSSettingsMenu';
import OperatingInformation from './OperatingInformation/OperatingInformation';
import ProFormaIncome from './ProFormaIncome/ProFormaIncome';
import ProFormaExpenses from './ProFormaExpenses/ProFormaExpenses';
import ProFormaPermanentDebt from './ProFormaPermanentDebt/ProFormaPermanentDebt';
import './ProFormaOperations.scss';
import { useProFormaSocketContext } from '../socketContext/ProFormaSocketProvider';
import BOEDiscrepancyIcon from '../../BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { hasModelFeature, isAllApartmentModels, ProFormaFeature } from 'helpers/proForma';
import RecoverableExpensesModal from './ProFormaExpenses/RecoverableExpensesModal/RecoverableExpensesModal';

export interface IProFormaOperationsProps {
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    hasWriteAccessToProForma: boolean;
    signUpMode?: boolean;
    boeDiscrepancies?: BOEProFormaDiscrepancies;
}

const ProFormaOperations = ({
    hasWriteAccessToProForma, signUpMode, navigateToSection, boeDiscrepancies,
}: IProFormaOperationsProps) => {
    const {
        proForma, operatingInfo, handleUpdateProFormaOperations, periodCadence,
        cashFlowPeriodsByCadence, formatDisplayValue, showCents,
        getLocalStorageValueByKey,
    } = useProFormaSocketContext();

    const [includePermanentDebt, setIncludePermanentDebt] = useState(false);
    const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
    const [includeGainLossToLease, setIncludeGainLossToLease] = useState(false);
    const [isRecoverableExpensesModalOpen, setIsRecoverableExpensesModalOpen] = useState(false);

    const localStorageHideBOEWarnings = Boolean(getLocalStorageValueByKey(HIDE_WARNINGS_KEY));

    const handleTogglePermanentDebt = () => {
        setIncludePermanentDebt(prev => !prev);
        handleUpdateProFormaOperations(operatingInfo.id, { permanentDebt: { includePermanentDebt: !includePermanentDebt } });
    };

    const handleToggleGainLossToLease = () => {
        setIncludeGainLossToLease(prev => !prev);
        handleUpdateProFormaOperations(operatingInfo.id, { includeGainLossToLease: !includeGainLossToLease });
    };

    const toggleSettingsMenu = () => {
        setIsSettingsMenuOpen(!isSettingsMenuOpen);
    };

    useEffect(() => {
        if (operatingInfo) {
            setIncludePermanentDebt(operatingInfo?.includePermanentDebt || false);
            setIncludeGainLossToLease(operatingInfo?.includeGainLossToLease || false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operatingInfo.includePermanentDebt, operatingInfo.includeGainLossToLease]);

    let operationBeginIndex = operatingInfo.operationBeginMonth || 0;
    if (periodCadence === Periods.ANNUALLY) {
        operationBeginIndex = Math.ceil(operationBeginIndex / 12);
    }

    const saleIndex = getSaleIndexForCadence(operatingInfo.saleMonth, periodCadence);

    const projectionPeriods = formatCashFlowPeriods(
        cashFlowPeriodsByCadence, periodCadence, !operatingInfo.operationStartDate, saleIndex,
    );

    const SETTINGS_MENU_ITEMS = [
        {
            key: 'Include gain/loss to lease',
            label: 'Include gain/loss to lease',
            visible: isAllApartmentModels(proForma.proFormaModelId),
            rightComponent: (
                <NSSwitch
                    id="include-gain-loss-to-lease"
                    name="include-gain-loss-to-lease"
                    checked={includeGainLossToLease}
                    onChange={handleToggleGainLossToLease}
                    containerClassName="text-dark heap-deal-pro-forma-settings-hide-boe-warning"
                />
            ),
        },
    ];

    const baseCashFlowColumns: ColumnDef<any>[] = projectionPeriods.map((periodInfo, index) => ({
        accessorFn: row => row.id,
        id: `${periodCadence}-${index}`,
        header: () => (
            <>
                <div
                    className={classNames([
                        ['h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right my-0'], {
                            'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                        },
                    ])}
                >
                    {periodInfo.period}
                </div>
                <div
                    className={classNames([
                        ['h6 text-uppercase text-right text-nowrap mt-1 mb-0'], {
                            'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                        },
                    ])}
                >
                    {periodInfo.label}
                </div>
            </>
        ),
        cell: ({ row, getValue }) => (
            <span className={tableValueClassName(getValue())}>
                <RenderIf isTrue={!periodInfo.isPlaceholder}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue() as any, showCents, 'currency')}
                </RenderIf>
            </span>
        ),
    }));

    return (
        <>
            <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                <ExpandableSection
                    number={OPERATIONS_EXPANDABLE_SECTIONS[0].number}
                    title={OPERATIONS_EXPANDABLE_SECTIONS[0].title}
                    subtitle={OPERATIONS_EXPANDABLE_SECTIONS[0].subtitle}
                    className="NSCard--level-2"
                    isExpanded
                    animate
                >
                    <OperatingInformation signUpMode={signUpMode} navigateToSection={navigateToSection} />
                </ExpandableSection>
                <ExpandableSection
                    number={OPERATIONS_EXPANDABLE_SECTIONS[1].number}
                    title={OPERATIONS_EXPANDABLE_SECTIONS[1].title}
                    subtitle={OPERATIONS_EXPANDABLE_SECTIONS[1].subtitle}
                    className="mt-3 NSCard--level-2"
                    warningIcon={(
                        <BOEDiscrepancyIcon
                            section={OPERATIONS_EXPANDABLE_SECTIONS[1].title}
                            metricNames={boeDiscrepancies ? boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[1].number] : []}
                            className="mr-1 my-auto"
                        />
                    )}
                    sectionButtons={(
                        <NSSettingsMenu
                            data-testid="income-settings-menu"
                            isMenuOpen={isSettingsMenuOpen}
                            toggleMenu={toggleSettingsMenu}
                            menuItems={SETTINGS_MENU_ITEMS}
                        />
                    )}
                    showWarningIcon={boeDiscrepancies && !localStorageHideBOEWarnings
                        ? !!boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[1].number]?.length : false}
                    displaySectionButtons={isAllApartmentModels(proForma.proFormaModelId)}
                    isExpanded
                    animate
                >
                    <ProFormaIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </ExpandableSection>
                <ExpandableSection
                    number={OPERATIONS_EXPANDABLE_SECTIONS[2].number}
                    title={OPERATIONS_EXPANDABLE_SECTIONS[2].title}
                    subtitle={OPERATIONS_EXPANDABLE_SECTIONS[2].subtitle}
                    className="mt-3 NSCard--level-2"
                    warningIcon={(
                        <BOEDiscrepancyIcon
                            section={OPERATIONS_EXPANDABLE_SECTIONS[2].title}
                            metricNames={boeDiscrepancies ? boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[2].number] : []}
                            className="mr-1 my-auto"
                        />
                    )}
                    showWarningIcon={boeDiscrepancies && !localStorageHideBOEWarnings
                        ? !!boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[2].number]?.length : false}
                    isExpanded
                    animate
                    displaySectionButtons={hasModelFeature(proForma.proFormaModelId, ProFormaFeature.RECOVERABLE_EXPENSES)}
                    sectionButtons={(
                        <NSButton
                            text="Edit recoverable expenses"
                            className="NSButton--secondary-lighten mr-2"
                            callback={() => setIsRecoverableExpensesModalOpen(true)}
                        />
                    )}
                >
                    <ProFormaExpenses
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </ExpandableSection>
                <ExpandableSection
                    number={OPERATIONS_EXPANDABLE_SECTIONS[3].number}
                    title={OPERATIONS_EXPANDABLE_SECTIONS[3].title}
                    subtitle={OPERATIONS_EXPANDABLE_SECTIONS[3].subtitle}
                    className="mt-3 NSCard--level-2"
                    isExpanded={includePermanentDebt}
                    animate
                    customToggle={(
                        <NSSwitch
                            id="include-permanent-debt"
                            name="include-permanent-debt"
                            containerClassName="ProFormaOperations__permanent-debt-toggle"
                            checked={includePermanentDebt}
                            onChange={e => e.preventDefault()}
                        />
                    )}
                    onCustomToggle={handleTogglePermanentDebt}
                >
                    <ProFormaPermanentDebt
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </ExpandableSection>
            </ReadOnlyWrapper>
            <RecoverableExpensesModal
                isOpen={isRecoverableExpensesModalOpen}
                toggle={() => setIsRecoverableExpensesModalOpen(false)}
            />        
        </>
    );
};

export default ProFormaOperations;
